import { useCallback, useEffect, useState } from 'react'
import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { listMembers } from '@/features/hr/application/list-members'
import { listBalances } from '@/features/hr/application/list-balances'
import { listTimeOffRequests } from '@/features/hr/application/list-time-off-requests'
import { listAllWorkflows } from '@/features/workflows/application/list-workflows'
import { members as hrMembers, timeoff, notify as hrNotify } from '@/features/hr/wiring'
import { workflows as workflowsPort, notify as workflowsNotify } from '@/features/workflows/wiring'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import type { OverviewSummary } from '../domain/overview'

const EMPTY_SUMMARY: OverviewSummary = {
	vacationDaysAvailable: 0,
	myPendingRequestsCount: 0,
	myApprovedRequestsCount: 0,
	teamPendingApprovalsCount: 0,
	activeTeamMembersCount: 0,
	activeWorkflowsCount: 0,
}

const useOverviewSummary = () => {
	const user = useSelector(() => session$.user.get())
	const canViewMembers = usePermission('hr.members.view')
	const canApproveDept = usePermission('hr.time-off.approve-dept')
	const canApproveHr = usePermission('hr.time-off.approve-hr')
	const canApprove = canApproveDept || canApproveHr

	const [summary, setSummary] = useState<OverviewSummary>(EMPTY_SUMMARY)
	const [isLoading, setIsLoading] = useState(true)

	const loadOwnData = useCallback(async () => {
		if (!user?.email) return

		const [allMembers, workflows] = await Promise.all([
			listMembers(hrMembers, { q: null, status: null }, hrNotify),
			listAllWorkflows(workflowsPort, workflowsNotify),
		])

		const activeWorkflowsCount = workflows.filter(w => w.status === 'active').length
		const ownMember = allMembers.find(m => m.email === user.email)

		if (!ownMember) {
			setSummary(prev => ({ ...prev, activeWorkflowsCount }))
			setIsLoading(false)
			return
		}

		const [balances, ownRequests] = await Promise.all([
			listBalances(timeoff, ownMember.id, hrNotify),
			listTimeOffRequests(
				timeoff,
				{ q: null, memberId: ownMember.id, memberIds: null, approvalStage: null, type: null, start: null, end: null },
				hrNotify,
			),
		])

		const vacBal = balances.find(b => b.type === 'vacation')
		const vacationDaysAvailable = vacBal?.available ?? 0

		const myPendingRequestsCount = ownRequests.filter(
			r =>
				r.deptApproval.status === 'pending' ||
				(r.deptApproval.status === 'approved' && r.hrApproval.status === 'pending'),
		).length

		const myApprovedRequestsCount = ownRequests.filter(
			r => r.deptApproval.status === 'approved' && r.hrApproval.status === 'approved',
		).length

		setSummary(prev => ({
			...prev,
			vacationDaysAvailable,
			myPendingRequestsCount,
			myApprovedRequestsCount,
			activeWorkflowsCount,
		}))
		setIsLoading(false)
	}, [user?.email])

	const loadMemberCount = useCallback(async () => {
		if (!canViewMembers) return
		const active = await listMembers(hrMembers, { q: null, status: 'active' }, hrNotify)
		setSummary(prev => ({ ...prev, activeTeamMembersCount: active.length }))
	}, [canViewMembers])

	const loadTeamApprovals = useCallback(async () => {
		if (!canApprove) return

		const all = await listTimeOffRequests(
			timeoff,
			{ q: null, memberId: null, memberIds: null, approvalStage: null, type: null, start: null, end: null },
			hrNotify,
		)

		let count = 0
		if (canApproveDept) {
			count += all.filter(r => r.deptApproval.status === 'pending').length
		}
		if (canApproveHr) {
			count += all.filter(r => r.deptApproval.status === 'approved' && r.hrApproval.status === 'pending').length
		}

		setSummary(prev => ({ ...prev, teamPendingApprovalsCount: count }))
	}, [canApprove, canApproveDept, canApproveHr])

	useEffect(() => {
		loadOwnData()
	}, [loadOwnData])

	useEffect(() => {
		loadMemberCount()
	}, [loadMemberCount])

	useEffect(() => {
		loadTeamApprovals()
	}, [loadTeamApprovals])

	return { summary, isLoading, canViewMembers, canApprove }
}

export { useOverviewSummary }
